// PRO Drift Auditor -- executive summary banner.
//
// The hero headline of the dashboard: net AI alpha, optimal corridor, current
// regime, and the dollar cost of inefficiency, rendered as institutional KPIs.

import React from 'react';
import { Activity, ShieldCheck, TrendingUp, Zap } from 'lucide-react';

import { fmtMoney, fmtMoneySigned, fmtPctSigned } from '../format';
import { REGIME_META, type ProExecutiveSummaryBanner } from '../types';

interface KpiProps {
    icon: React.ReactNode;
    label: string;
    value: string;
    sub?: string;
    tone?: 'emerald' | 'indigo' | 'rose' | 'amber';
}

const TONES: Record<NonNullable<KpiProps['tone']>, string> = {
    emerald: 'from-emerald-500/20 to-emerald-500/5 text-emerald-300 ring-emerald-400/30',
    indigo: 'from-indigo-500/20 to-indigo-500/5 text-indigo-300 ring-indigo-400/30',
    rose: 'from-rose-500/20 to-rose-500/5 text-rose-300 ring-rose-400/30',
    amber: 'from-amber-500/20 to-amber-500/5 text-amber-300 ring-amber-400/30',
};

const Kpi: React.FC<KpiProps> = ({ icon, label, value, sub, tone = 'indigo' }) => (
    <div className={`relative rounded-xl bg-gradient-to-b ${TONES[tone]} ring-1 p-4 backdrop-blur`}>
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider opacity-90">
            {icon}
            {label}
        </div>
        <div className="mt-1.5 text-2xl font-black tabular-nums text-white">{value}</div>
        {sub && <div className="mt-0.5 text-[11px] text-slate-300/80">{sub}</div>}
    </div>
);

export const ExecutiveBanner: React.FC<{ banner: ProExecutiveSummaryBanner }> = ({ banner }) => {
    const regime = REGIME_META[banner.active_regime_state];
    // Display the live macro regime name (single source of truth from the
    // macro_regime domain, e.g. "Slowdown"); fall back to the internal
    // simulation label only when macro data is unavailable.
    const regimeName = banner.macro_regime_name ?? regime.label;
    return (
        <section className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 p-6 shadow-xl">
            <div
                className="pointer-events-none absolute inset-0 opacity-70"
                aria-hidden
                style={{
                    background:
                        'radial-gradient(60% 80% at 15% 0%, rgba(99,102,241,0.25), transparent), radial-gradient(50% 70% at 100% 100%, rgba(139,92,246,0.22), transparent)',
                }}
            />
            <div className="relative">
                <div className="flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 px-3 py-1 text-[11px] font-black uppercase tracking-wider text-white shadow-lg shadow-indigo-500/30">
                        <Zap className="h-3.5 w-3.5" /> Forge Ultra Regime-Aware Audit
                    </span>
                    <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider ring-1 ring-inset ${regime.text} ${regime.bg} ring-current/20`}
                    >
                        <Activity className="h-3.5 w-3.5" /> {regimeName}
                    </span>
                </div>

                <h2 className="mt-4 max-w-4xl text-xl font-black leading-tight text-white md:text-2xl">
                    {banner.headline}
                </h2>
                <p className="mt-2 max-w-4xl text-sm leading-relaxed text-slate-300">
                    {banner.subheadline}
                </p>

                <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
                    <Kpi
                        icon={<TrendingUp className="h-3.5 w-3.5" />}
                        label="Alpha vs Drift"
                        value={fmtMoneySigned(banner.net_ai_alpha_vs_drift_dollars)}
                        sub={`${fmtPctSigned(banner.cagr_edge_vs_drift_pct)} CAGR edge`}
                        tone="emerald"
                    />
                    <Kpi
                        icon={<Zap className="h-3.5 w-3.5" />}
                        label="Alpha vs Best Static"
                        value={fmtMoneySigned(banner.net_ai_alpha_vs_best_static_dollars)}
                        sub="net of fees & tax"
                        tone="indigo"
                    />
                    <Kpi
                        icon={<ShieldCheck className="h-3.5 w-3.5" />}
                        label="Optimal Corridor"
                        value={`±${banner.optimal_corridor_threshold_pct}%`}
                        sub="max-Sharpe rebalance band"
                        tone="amber"
                    />
                    <Kpi
                        icon={<Activity className="h-3.5 w-3.5" />}
                        label="Inefficiency Cost"
                        value={fmtMoney(banner.total_inefficiency_cost_dollars)}
                        sub={`${banner.max_single_asset_bloat_ticker} drifted ${banner.max_single_asset_bloat_pct.toFixed(1)}%`}
                        tone="rose"
                    />
                </div>
            </div>
        </section>
    );
};
